<template>
	<span>{{ n(currentValue) }}</span>
</template>

<script setup lang="ts">
const props = withDefaults(defineProps<{
		end: number,
		start?: number,
		duration?: number,
		decimalPlaces?: number,
		autoplay?: boolean,
		easing?: boolean,
	}>(),
	{
		start: 0,
		duration: 2,
		decimalPlaces: 0,
		autoplay: true,
		easing: true
	}
)

const {n} = useI18n();

const currentValue = ref(props.start);

const smartEasingThreshold = 999; // smooth easing for large numbers above this if useEasing (999)
const smartEasingAmount = 333; // amount to be eased for numbers above threshold (333)

let startVal: number = props.start;
let endVal: number = props.end;
let finalEndVal: number|null = null;
let paused: boolean = true;
let countDown: boolean = false;
let useEasing: boolean = props.easing;
let duration: number = props.duration * 1000
let rAF: number = 0;
let startTime: number|null = null;
let remaining: number|null = null;

function count(timestamp: number): void {
	if (!startTime) { startTime = timestamp; }

	const progress = timestamp - startTime;
	remaining = duration - progress;

	// to ease or not to ease
	if (useEasing) {
		if (countDown) {
			currentValue.value = startVal - easeOutExpo(progress, 0, startVal - endVal, duration);
		} else {
			currentValue.value = easeOutExpo(progress, startVal, endVal - startVal, duration);
		}
	} else {
		currentValue.value = startVal + (endVal - startVal) * (progress / duration);
	}

	// don't go past endVal since progress can exceed duration in the last frame
	const wentPast = countDown ? currentValue.value < endVal : currentValue.value > endVal;
	currentValue.value = wentPast ? endVal : currentValue.value;

	// decimal
	currentValue.value = Number(currentValue.value.toFixed(props.decimalPlaces));

	// whether to continue
	if (progress < duration) {
		rAF = requestAnimationFrame(count);
	} else if (finalEndVal !== null) {
		// smart easing
		update(finalEndVal);
	}
}

function update(newEndVal: number): void {
	cancelAnimationFrame(rAF);
	startTime = null;
	endVal = newEndVal;
	if (endVal === currentValue.value) {
		return;
	}
	startVal = currentValue.value;
	if (finalEndVal == null) {
		resetDuration();
	}
	finalEndVal = null;
	determineDirectionAndSmartEasing();
	rAF = requestAnimationFrame(count);
}

function resetDuration(): void {
	startTime = null;
	duration = props.duration * 1000;
	remaining = duration;
}

function reset(): void {
	cancelAnimationFrame(rAF);
	paused = true;
	resetDuration();
	startVal = props.start;
	currentValue.value = startVal;
}

function easeOutExpo(currentTime: number, startValue: number, changeValue: number, duration: number): number {
	return changeValue * (-Math.pow(2, -10 * currentTime / duration) + 1) * 1024 / 1023 + startValue
}

function start() {
	if (props.duration > 0) {
		determineDirectionAndSmartEasing();
		paused = false;
		rAF = requestAnimationFrame(count);
	} else {
		currentValue.value = endVal;
	}
}

 /**
 * Smart easing works by breaking the animation into 2 parts, the second part being the
 * smartEasingAmount and first part being the total amount minus the smartEasingAmount. It works
 * by disabling easing for the first part and enabling it on the second part. It is used if
 * useEasing is true and the total animation amount exceeds the smartEasingThreshold.
 */
 function determineDirectionAndSmartEasing(): void {
	const end = (finalEndVal) ?? endVal;
	countDown = (startVal > end);
	const animateAmount = end - startVal;
	if (Math.abs(animateAmount) > smartEasingThreshold && props.easing) {
	finalEndVal = end;
	const up = (countDown) ? 1 : -1;
	endVal = end + (up * smartEasingAmount);
	duration = duration / 2;
} else {
	endVal = end;
	finalEndVal = null;
}
if (finalEndVal !== null) {
	// setting finalEndVal indicates smart easing
	useEasing = false;
} else {
	useEasing = props.easing;
}
}

watch([() => props.start, () => props.end], () => {
	startVal = props.start
	endVal = props.end
	if (props.autoplay) {
		reset()
		start()
	}
})

onMounted(() => {
	if (props.autoplay) {
		start()
	}
})

onUnmounted(() => {
	reset()
})

defineExpose({
	start
})
</script>